/**
 * Hook do DV01 do IPCA (#29) — popula o header DV01 Total/Next + a tabela DV01
 * por ano do PnL/Mesa. Endpoint computa LIVE (re-roda a valuation com a implícita
 * #218 base e +1bp), fora do hot-path do pnl_historico → cache module-level TTL
 * 10min + startVisiblePolling, padrão da casa (#75). Erro → null + flag; o
 * consumidor mantém o "—" honesto.
 */
import { useEffect, useState } from "react";
import { getDv01Ipca } from "../api/client";
import type { Dv01IpcaResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<Dv01IpcaResponse>;
}

let cache: CacheEntry | null = null;

export function invalidateDv01IpcaCache(): void {
  cache = null;
}

export interface UseDv01IpcaState {
  data: Dv01IpcaResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function useDv01Ipca(): UseDv01IpcaState {
  const [state, setState] = useState<UseDv01IpcaState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;

    function load() {
      const now = Date.now();
      if (!cache || cache.expiresAt < now) {
        cache = { expiresAt: now + REFETCH_MS, promise: getDv01Ipca() };
      }
      cache.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache = null;
          setState({ data: null, isLoading: false, error: err });
        });
    }

    load();
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, []);

  return state;
}
